import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { MemberDefaults } from '../system/defaults';
import { useService } from './ServiceContext';
import { useFeatureFlags } from './FeatureFlagsContext';
import { FeatureFlags } from '../flags';

interface DefaultsContextType {
  defaults: () => Promise<MemberDefaults>;
  setContextDefaults: Dispatch<SetStateAction<MemberDefaults | undefined>>;
}

interface DefaultsProps {
  children: ReactNode;
}

export const DefaultsContext = createContext<DefaultsContextType | undefined>(
  undefined,
);

// TODO: move this container to FLC (MARK-1579)
export const DefaultsProvider: React.FC<DefaultsProps> = ({ children }) => {
  const { getMemberDefaults } = useService();
  const { hasFeature, isLoading } = useFeatureFlags();

  const taskRef = useRef<Promise<MemberDefaults> | null>(null);
  const getMemberDefaultsRef = useRef(getMemberDefaults);
  const hasInitializedRef = useRef(false);
  const [contextDefaults, setContextDefaults] = useState<
    MemberDefaults | undefined
  >(undefined);

  // Update the ref when getMemberDefaults changes but keep the same defaults function
  getMemberDefaultsRef.current = getMemberDefaults;

  // Use useCallback with empty deps to ensure the function reference never changes
  const defaults = useCallback(async (): Promise<MemberDefaults> => {
    // if defaults exist in state, return those. This gets updated in <SetFieldDefaults />
    if (typeof contextDefaults !== 'undefined') {
      return contextDefaults;
    }

    // If we already have a promise, return it
    if (taskRef.current) {
      return taskRef.current;
    }

    // Wait for feature flags to be loaded (avoid undefined state)
    const featureEnabled = hasFeature(FeatureFlags.NGW_EDIT_MEMBER_DEFAULTS);
    if (!featureEnabled) {
      const promise = Promise.resolve({} as MemberDefaults);

      taskRef.current = promise;

      return promise;
    }

    // Use the ref to get the current getMemberDefaults function
    const promise = (async () => {
      try {
        return await getMemberDefaultsRef.current();
      } catch (error) {
        // Reset the ref on error so we can retry
        taskRef.current = null;
        throw error;
      }
    })();

    // Store the promise immediately to prevent race conditions
    taskRef.current = promise;
    return promise;
  }, [hasFeature, contextDefaults]);

  // Eagerly load defaults after authentication (when this provider mounts)
  useEffect(() => {
    // Only initialize once, even if useEffect runs multiple times
    if (isLoading || hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;

    defaults().catch((error) => {
      console.error('Failed to preload member defaults:', error);

      // Reset initialization flag on error so we can retry
      hasInitializedRef.current = false;
    });
  }, [defaults, isLoading]);

  return (
    <DefaultsContext.Provider value={{ defaults, setContextDefaults }}>
      {children}
    </DefaultsContext.Provider>
  );
};

/**
 * Hook to use defaults context
 */
export const useDefaults = (): DefaultsContextType => {
  const context = useContext(DefaultsContext);
  if (context === undefined) {
    throw new Error('useDefaults must be used within a DefaultsProvider');
  }
  return context;
};
